import { ReactNode, useEffect, useMemo, useState } from 'react';

import './PwaInstallGuide.scss';

import CloseIcon from '@mui/icons-material/Close';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import IosShareIcon from '@mui/icons-material/IosShare';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import StarIcon from '@mui/icons-material/Star';

import LogoOnly from '../../../assets/images/logos/logoonly.svg';
import Config from '../../../config';
import { getPwaIconUrl, PWA_ICON_192 } from '../constants';
import { PwaPlatform } from '../types';

export type PwaInstallGuideTab = 'preview' | 'android' | 'ios';

export interface IPwaInstallGuideProps {
    open: boolean;
    platform: PwaPlatform;
    canPromptNativeInstall: boolean;
    onClose: () => void;
    onInstall: () => void;
}

function resolveInitialTab(platform: PwaPlatform): PwaInstallGuideTab {
    if (platform === 'ios') {
        return 'ios';
    }
    if (platform === 'android') {
        return 'android';
    }
    return 'preview';
}

function RatingStars() {
    return (
        <span className="pwa-install-guide__stars" aria-label="4,5 von 5 Sternen">
            <StarIcon fontSize="inherit" />
            <StarIcon fontSize="inherit" />
            <StarIcon fontSize="inherit" />
            <StarIcon fontSize="inherit" />
            <StarHalfIcon fontSize="inherit" />
        </span>
    );
}

function StepCard({ step, title, description, visual }: { step: number; title: string; description: string; visual?: ReactNode }) {
    return (
        <article className="pwa-install-guide__step">
            <div className="pwa-install-guide__step-num" aria-hidden>
                {step}
            </div>
            <div className="pwa-install-guide__step-body">
                <h3 className="pwa-install-guide__step-title">{title}</h3>
                <p className="pwa-install-guide__step-desc">{description}</p>
                {visual ? <div className="pwa-install-guide__step-visual">{visual}</div> : null}
            </div>
        </article>
    );
}

export function PwaInstallGuide({ open, platform, canPromptNativeInstall, onClose, onInstall }: IPwaInstallGuideProps) {
    const siteName = Config.GLOBAL_SITE_NAME;
    const bonusCoins = Config.PWA_INSTALL_BONUS_COINS;
    const coinLabel = Config.GLOBAL_SITE_COINS;
    const pwaIconSrc = getPwaIconUrl(PWA_ICON_192, Config.PWA_ICONS_BASE_URL);

    const [tab, setTab] = useState<PwaInstallGuideTab>(() => resolveInitialTab(platform));
    const [iconSrc, setIconSrc] = useState(pwaIconSrc);

    useEffect(() => {
        if (open) {
            setTab(resolveInitialTab(platform));
            setIconSrc(pwaIconSrc);
        }
    }, [open, platform, pwaIconSrc]);

    const tabs = useMemo(
        () =>
            [
                { id: 'preview' as const, label: 'Vorschau' },
                { id: 'android' as const, label: 'Android' },
                { id: 'ios' as const, label: 'iPhone / iOS' },
            ] as const,
        []
    );

    const handleInstallClick = () => {
        if (canPromptNativeInstall) {
            onInstall();
            return;
        }
        if (platform === 'ios') {
            setTab('ios');
            return;
        }
        if (platform === 'android') {
            setTab('android');
            return;
        }
        onInstall();
    };

    if (!open) {
        return null;
    }

    return (
        <div className="pwa-install-guide" role="dialog" aria-modal="true" aria-labelledby="pwa-install-guide-title">
            <button type="button" className="pwa-install-guide__backdrop" aria-label="Schließen" onClick={onClose} />

            <div className="pwa-install-guide__sheet">
                <header className="pwa-install-guide__appbar">
                    <img
                        className="pwa-install-guide__icon"
                        src={iconSrc}
                        alt=""
                        width={56}
                        height={56}
                        onError={() => {
                            if (iconSrc !== LogoOnly) {
                                setIconSrc(LogoOnly);
                            }
                        }}
                    />
                    <div className="pwa-install-guide__app-meta">
                        <h2 id="pwa-install-guide-title" className="pwa-install-guide__app-name">
                            {siteName}
                        </h2>
                        <div className="pwa-install-guide__app-sub">
                            <RatingStars />
                            <span className="pwa-install-guide__free">Kostenlos</span>
                        </div>
                    </div>
                    <button type="button" className="pwa-install-guide__install-btn" onClick={handleInstallClick}>
                        Installieren
                    </button>
                    <button type="button" className="pwa-install-guide__close" aria-label="Schließen" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </button>
                </header>

                <div className="pwa-install-guide__tabs" role="tablist" aria-label="Installationshilfe">
                    {tabs.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            role="tab"
                            aria-selected={tab === item.id}
                            className={`pwa-install-guide__tab${tab === item.id ? ' is-active' : ''}`}
                            onClick={() => setTab(item.id)}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>

                <div className="pwa-install-guide__body">
                    {tab === 'preview' ? (
                        <div className="pwa-install-guide__preview">
                            <span className="pwa-install-guide__pill">Weltweit verfügbar · iOS &amp; Android</span>
                            <h3 className="pwa-install-guide__headline">
                                Dating App – <em>echte Verbindungen</em>, die zählen
                            </h3>
                            <p className="pwa-install-guide__lead">
                                Installiere {siteName} auf dem Homescreen — fühlt sich an wie aus dem App Store, ohne Store-Download.
                            </p>
                            {bonusCoins > 0 ? (
                                <p className="pwa-install-guide__bonus">
                                    Bonus:{' '}
                                    <strong>
                                        +{bonusCoins} {coinLabel}
                                    </strong>{' '}
                                    nach der Installation.
                                </p>
                            ) : null}
                            <button type="button" className="pwa-install-guide__cta" onClick={handleInstallClick}>
                                Kostenlos installieren
                            </button>
                            <p className="pwa-install-guide__footnote">Die App funktioniert wie eine native App — kein App Store nötig.</p>
                        </div>
                    ) : null}

                    {tab === 'android' ? (
                        <div className="pwa-install-guide__steps">
                            {canPromptNativeInstall ? (
                                <>
                                    <StepCard
                                        step={1}
                                        title="Banner oben antippen"
                                        description="Tippe auf „Installieren“ im Banner am oberen Seitenrand — oder hier auf Installieren."
                                    />
                                    <StepCard
                                        step={2}
                                        title="Installation bestätigen"
                                        description={`Ein Dialog erscheint — tippe auf „Installieren“, um ${siteName} deinem Homescreen hinzuzufügen.`}
                                    />
                                    <StepCard
                                        step={3}
                                        title="App öffnen & Bonus erhalten"
                                        description={`Öffne ${siteName} vom Homescreen aus — dein Bonus wird automatisch gutgeschrieben.`}
                                    />
                                </>
                            ) : (
                                <>
                                    <StepCard
                                        step={1}
                                        title="Chrome-Menü öffnen"
                                        description="Tippe oben rechts in Chrome auf die drei Punkte (⋮)."
                                    />
                                    <StepCard
                                        step={2}
                                        title={'„App installieren“ / „Zum Startbildschirm“'}
                                        description={`Wähle diesen Eintrag und bestätige — ${siteName} wird als App hinzugefügt.`}
                                    />
                                    <StepCard
                                        step={3}
                                        title="App öffnen & Bonus erhalten"
                                        description={`Öffne ${siteName} vom Homescreen aus — dein Bonus wird automatisch gutgeschrieben.`}
                                    />
                                </>
                            )}
                            <p className="pwa-install-guide__footnote">Die App funktioniert wie eine native App — kein App Store nötig.</p>
                        </div>
                    ) : null}

                    {tab === 'ios' ? (
                        <div className="pwa-install-guide__steps">
                            <StepCard
                                step={1}
                                title="Teilen-Button antippen"
                                description="Tippe unten in Safari auf den Teilen-Button."
                                visual={
                                    <span className="pwa-install-guide__chip">
                                        <IosShareIcon fontSize="small" />
                                        Teilen-Symbol
                                    </span>
                                }
                            />
                            <StepCard
                                step={2}
                                title={'„Zum Home-Bildschirm“ wählen'}
                                description="Scrolle im Menü nach unten und tippe auf diesen Eintrag."
                                visual={
                                    <span className="pwa-install-guide__chip">
                                        <HomeOutlinedIcon fontSize="small" />
                                        Zum Home-Bildschirm
                                    </span>
                                }
                            />
                            <StepCard
                                step={3}
                                title={'Mit „Hinzufügen“ bestätigen'}
                                description={`Tippe oben rechts auf „Hinzufügen“ — ${siteName} erscheint als App-Icon auf deinem Homescreen.`}
                            />
                            <StepCard
                                step={4}
                                title="App öffnen & Bonus erhalten"
                                description={`Öffne ${siteName} vom Homescreen — dein Bonus wird automatisch gutgeschrieben.`}
                            />
                            <p className="pwa-install-guide__footnote">
                                Kein App Store nötig — funktioniert direkt im Safari-Browser auf deinem iPhone.
                            </p>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export default PwaInstallGuide;
